import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Code as CodeIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const PromptCodeBlock = ({ label, content, fallback }) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    <Paper
      sx={{
        p: 2,
        bgcolor: 'grey.100',
        maxHeight: '60vh',
        overflow: 'auto',
        fontFamily: 'monospace'
      }}
    >
      <Typography
        variant="body2"
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'inherit',
          fontSize: '0.875rem',
          lineHeight: 1.4
        }}
      >
        {content || fallback}
      </Typography>
    </Paper>
  </Box>
);

export function PromptViewButton({ variable, onViewPrompt }) {
  if (!variable.systemPrompt && !variable.userPrompt && !variable.completePrompt) {
    return null;
  }
  return (
    <IconButton
      size="small"
      onClick={() => onViewPrompt(variable)}
      title="View Prompt"
      sx={{ ml: 1 }}
    >
      <CodeIcon fontSize="small" />
    </IconButton>
  );
}

// eslint-disable-next-line max-lines-per-function
export default function PromptDetailDialog({
  open,
  onClose,
  selectedVariable,
  tabValue,
  onTabChange,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Prompt Details: {selectedVariable?.variableName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <ExpandLessIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => onTabChange(newValue)}
            aria-label="prompt tabs"
          >
            <Tab label="Complete Prompt" />
            <Tab label="System Prompt" />
            <Tab label="User Prompt" />
            <Tab label="Variable Info" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <PromptCodeBlock
            label="Complete Prompt as Sent to LLM"
            content={selectedVariable?.completePrompt}
            fallback="Prompt information not available for this variable."
          />
        )}

        {tabValue === 1 && (
          <PromptCodeBlock
            label="System Prompt"
            content={selectedVariable?.systemPrompt}
            fallback="System prompt not available for this variable."
          />
        )}

        {tabValue === 2 && (
          <PromptCodeBlock
            label="User Prompt"
            content={selectedVariable?.userPrompt}
            fallback="User prompt not available for this variable."
          />
        )}

        {tabValue === 3 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Variable Information
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Variable Name:</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedVariable?.variableName}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Typography variant="body1">{selectedVariable?.status || 'completed'}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">Description:</Typography>
                  <Typography variant="body1">
                    {selectedVariable?.variableDescription || 'No description available'}
                  </Typography>
                </Grid>
                {selectedVariable?.mcpToolsUsed && selectedVariable.mcpToolsUsed.length > 0 && (
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">MCP Tools Used:</Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedVariable.mcpToolsUsed.map((tool, index) => (
                        <Chip key={index} label={tool} size="small" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedVariable?.result && (
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">Result:</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.50', mt: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedVariable.result}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedVariable?.error && (
                  <Grid size={12}>
                    <Typography variant="body2" color="error">Error:</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'error.light', mt: 1 }}>
                      <Typography variant="body2" color="error.contrastText">
                        {selectedVariable.error}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
